import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, Staff, Store } from '../api';
import { useAuth } from './AuthContext';

interface UserContextType {
  userProfile: Staff | null;
  stores: Store[];
  currentStore: Store | null;
  isManager: boolean;
  loading: boolean;
  refreshStores: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  userProfile: null,
  stores: [],
  currentStore: null,
  isManager: false,
  loading: true,
  refreshStores: async () => {},
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState<Staff | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);

  // スタッフプロフィールのリアルタイム購読
  useEffect(() => {
    if (!currentUser) {
      setUserProfile(null);
      setStores([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = api.listenToStaffProfile(currentUser.uid, (staff) => {
      setUserProfile(staff);
      setLoading(false);
    });

    // 店舗データの取得
    api.getStores().then(loadedStores => {
      setStores(loadedStores);
    }).catch(e => {
      console.error('店舗データの取得に失敗:', e);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const currentStore = stores.length > 0 ? stores[0] : null;
  const isManager = userProfile?.role === 'system_admin' || userProfile?.role === 'store_manager';

  const refreshStores = async () => {
    try {
      const loadedStores = await api.getStores();
      setStores(loadedStores);
    } catch (e) {
      console.error('店舗データの更新に失敗:', e);
    }
  };

  return (
    <UserContext.Provider value={{ userProfile, stores, currentStore, isManager, loading, refreshStores }}>
      {children}
    </UserContext.Provider>
  );
};
